import { algoliasearch } from 'algoliasearch';
import instantsearch from 'instantsearch.js';
import { searchBox, hits, configure, infiniteHits, stats } from 'instantsearch.js/es/widgets';

// Only initialize if Algolia credentials are available
if (window.algoliaAppId && window.algoliaApiKey && window.algoliaIndex) {
	const searchClient = algoliasearch(window.algoliaAppId, window.algoliaApiKey);
    const isSearchPage = document.body.classList.contains('search');
	
	// Desktop header search
	const searchboxContainer = document.getElementById('searchbox');
	if (searchboxContainer) {
		initSearch(searchClient, 'desktop');
	}
	
	// Mobile header search
	const searchboxMobileContainer = document.getElementById('searchbox-mobile');
	if (searchboxMobileContainer) {
		initSearch(searchClient, 'mobile');
	}
	
	// Search page
	const searchPageContainer = document.getElementById('searchbox-page');
	if (searchPageContainer) {
		initSearchPage(searchClient);
	}
}

function initSearch(searchClient, type = 'desktop') {
	const isMobile = type === 'mobile';
	const searchboxSelector = isMobile ? '#searchbox-mobile' : '#searchbox';
	const hitsSelector = isMobile ? '#hits-mobile' : '#hits';
	
	const search = instantsearch({
		indexName: window.algoliaIndex,
		searchClient,
		routing: false,
		searchFunction(helper) {
			const resultsContainer = document.querySelector(hitsSelector);
			const seeAllInline = document.querySelector(`${searchboxSelector} .see-all-inline`);
			
			if (helper.state.query === '') {
				if (resultsContainer) resultsContainer.classList.remove('is-visible');
				if (seeAllInline) seeAllInline.remove();
				
				if (!isMobile) {
					document.body.classList.remove('search-open');
				}
				return;
			}
			
			if (resultsContainer) resultsContainer.classList.add('is-visible');
			
			if (!isMobile) {
				document.body.classList.add('search-open');
			}
			
			helper.search();
		}
	});

	search.addWidgets([
		configure({
			hitsPerPage: isMobile ? 6 : 8,
		}),

		searchBox({
			container: searchboxSelector,
			placeholder: 'Search...',
			showReset: false, // Disabled - we use the header X icon instead
			showSubmit: true,
			showLoadingIndicator: true,
			cssClasses: {
				root: 'search-box-root',
				form: 'search-box-form',
				input: 'search-box-input',
				submit: 'search-box-submit',
				reset: 'search-box-reset',
				loadingIndicator: 'search-box-loading',
			},
			templates: {
				submit({ cssClasses }, { html }) {
					return html`
						<div class="btn-arrow btn-arrow-light">
							<svg xmlns="http://www.w3.org/2000/svg" width="33.937" height="33.937" viewBox="0 0 33.937 33.937">
								<g transform="translate(33.937) rotate(90)">
									<path d="M0,0H33.937V33.937H0Z" fill="none"></path>
									<path d="M0,11.312l1.994,1.994,7.9-7.89V22.625h2.828V5.416l7.89,7.9,2.008-2.008L11.312,0Z" transform="translate(5.656 5.656)" fill="currentColor"></path>
								</g>
							</svg>
						</div>
					`;
				},
			},
		}),

		hits({
			container: hitsSelector,
			templates: {
				item(hit) {
					return `
						<a href="${hit.url}" class="hit">
							${hit.html}
						</a>
					`;
				},
				empty(results, { html }) {
					return html`
						<div class="no-results-container">
							<h3 class="no-results-title">Nothing found...</h3>
							<p class="no-results-message">We couldn't find any matches for "<strong>${results.query}</strong>".</p>
							<p class="no-results-hint">Please check your search for any spelling mistakes, or try using different keywords.</p>
						</div>
					`;
				},
			},
			cssClasses: {
				list: 'search-results-grid',
			},
		}),
	]);
    
    search.start();

    // Override Enter key to redirect to search page instead of just searching
    const searchInput = document.querySelector(`${searchboxSelector} .ais-SearchBox-input`);
    if (searchInput) {
        const form = searchInput.closest('form');
        if (form) {
            form.addEventListener('submit', function(e) {
                e.preventDefault();
                if (searchInput.value.length > 0) {
                    window.location.href = `/search?q=${encodeURIComponent(searchInput.value)}`;
                }
            });
        }
    }

	// Add "See all X results" inline link and footer
	search.on('render', () => {
		const searchboxContainer = document.querySelector(searchboxSelector);
		const hitsContainer = document.querySelector(hitsSelector);
		const input = document.querySelector(`${searchboxSelector} .ais-SearchBox-input`);
		const results = search.helper?.lastResults;
		
		if (!searchboxContainer || !input) return;
		
		// Remove existing "See all" inline link
		const existingSeeAll = searchboxContainer.querySelector('.see-all-inline');
		if (existingSeeAll) existingSeeAll.remove();
		
		// Add "See all X results" next to the arrow when there are results
		if (results && results.nbHits > 0 && input.value.length > 0) {
			const form = searchboxContainer.querySelector('.ais-SearchBox-form');
			if (form) {
				const seeAllLink = document.createElement('a');
				seeAllLink.href = `/search?q=${encodeURIComponent(input.value)}`;
				seeAllLink.className = 'see-all-inline';
				seeAllLink.innerHTML = `See all ${results.nbHits} results`;
				
				// Insert before the submit button
				const submitBtn = form.querySelector('.search-box-submit');
				if (submitBtn) {
					submitBtn.parentNode.insertBefore(seeAllLink, submitBtn);
				}
			}
		}
		
		// Also add footer in hits container
		if (hitsContainer && results) {
			const existingFooter = hitsContainer.querySelector('.search-results-footer');
			if (existingFooter) existingFooter.remove();
			
			const limit = isMobile ? 6 : 8;
			if (results.nbHits > limit && input.value.length > 0) {
				const footer = document.createElement('div');
				footer.className = 'search-results-footer';
				footer.innerHTML = `
					<a href="/search?q=${encodeURIComponent(input.value)}" class="see-all-results btn btn-dark">
						See all ${results.nbHits} results
						<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
							<line x1="5" y1="12" x2="19" y2="12"></line>
							<polyline points="12 5 19 12 12 19"></polyline>
						</svg>
					</a>
				`;
				hitsContainer.appendChild(footer);
			}
		}
	});

	// Input state handling (simplified - no reset button logic needed)
	const input = document.querySelector(`${searchboxSelector} .ais-SearchBox-input`);
	const form = document.querySelector(`${searchboxSelector} .ais-SearchBox-form`);

	if (input && form) {
		if (input.value.length > 0) {
			form.classList.add('has-value');
		}

		input.addEventListener('input', function() {
			if (input.value.length > 0) {
				form.classList.add('has-value');
			} else {
				form.classList.remove('has-value');
			}
		});
	}

	// Pre-fill from URL query param (desktop only, NOT on search page)
	if (!document.body.classList.contains('search')) {
		const urlParams = new URLSearchParams(window.location.search);
		const queryParam = urlParams.get('query') || urlParams.get('q');
		if (queryParam) {
			const searchInput = document.querySelector(`${searchboxSelector} .ais-SearchBox-input`);
			if (searchInput) {
				searchInput.value = queryParam;
				searchInput.dispatchEvent(new Event('input', { bubbles: true }));
			}
		}
	}
}

/**
 * Search page - full results with infinite scroll / load more
 */
function initSearchPage(searchClient) {
    const search = instantsearch({
        indexName: window.algoliaIndex,
        searchClient,
        routing: {
            stateMapping: {
                stateToRoute(uiState) {
                    const indexUiState = uiState[window.algoliaIndex] || {};
                    return {
                        q: indexUiState.query
                    }
                },
                routeToState(routeState) {
                    return {
                        [window.algoliaIndex]: {
                            query: routeState.q
                        }
                    }
                }
            },
        },
        searchFunction(helper) {
            const popularPages = document.getElementById('popular-pages');
            const hitsContainer = document.getElementById('hits-page');
            const statsContainer = document.getElementById('search-stats');
            
            if (helper.state.query === '') {
                if (popularPages) popularPages.style.display = 'block';
                if (hitsContainer) hitsContainer.style.display = 'none';
                if (statsContainer) statsContainer.style.display = 'none';
                document.body.classList.remove('search-page-has-query');
                return;
            }
            
            if (popularPages) popularPages.style.display = 'none';
            if (hitsContainer) hitsContainer.style.display = 'block';
            if (statsContainer) statsContainer.style.display = 'block';
            document.body.classList.add('search-page-has-query');
            helper.search();
        }
    });

    search.addWidgets([
        configure({
            hitsPerPage: 12,
        }),

        stats({
            container: '#search-stats',
            templates: {
                text(data, {html}) {
                    if (data.nbHits == 0) {
                        return html`<span style="display:none"></span>`;
                    }
                    return html`<span><strong>${data.nbHits} Results</strong></span>`;
                }
            },
            cssClasses: {
                root: 'search-stats'
            }
        }),

        searchBox({
            container: '#searchbox-page',
            placeholder: 'Enter search term',
            showReset: true,
            showSubmit: true,
            showLoadingIndicator: true,
            autofocus: true,
            cssClasses: {
                root: 'search-box-root',
                form: 'search-box-form',
                input: 'search-box-input',
                submit: 'search-box-submit',
                reset: 'search-box-reset hidden',
                loadingIndicator: 'search-box-loading',
            },
            templates: {
                submit({ cssClasses }, { html }) {
                    return html`
                        <div class="btn-arrow btn-arrow-light">
                            <svg xmlns="http://www.w3.org/2000/svg" width="33.937" height="33.937" viewBox="0 0 33.937 33.937">
                                <g transform="translate(33.937) rotate(90)">
                                    <path d="M0,0H33.937V33.937H0Z" fill="none"></path>
                                    <path d="M0,11.312l1.994,1.994,7.9-7.89V22.625h2.828V5.416l7.89,7.9,2.008-2.008L11.312,0Z" transform="translate(5.656 5.656)" fill="currentColor"></path>
                                </g>
                            </svg>
                        </div>
                    `;
                },
                reset({ cssClasses }, { html }) {
                    return html`
                        <div class="btn-reset">
                            <svg xmlns="http://www.w3.org/2000/svg" width="13.128" height="13.128" viewBox="0 0 13.128 13.128">
                                <path d="M162.128-1175.678,160.806-1177l-5.242,5.242L150.322-1177,149-1175.678l5.242,5.242L149-1165.194l1.322,1.322,5.242-5.242,5.242,5.242,1.322-1.322-5.242-5.242Z" transform="translate(-149 1177)" fill="currentColor" fill-rule="evenodd"/>
                            </svg>
                        </div>
                    `;
                },
            },
        }),

        infiniteHits({
            container: '#hits-page',
            templates: {
                item(hit) {
                    return `
                        <a href="${hit.url}" class="hit">
                            ${hit.html}
                        </a>
                    `;
                },
                empty(results, { html }) {
                    return html`
                        <div class="no-results-container">
                            <h3 class="no-results-title">Nothing found...</h3>
                            <p class="no-results-message">We couldn't find any matches for "<strong>${results.query}</strong>".</p>
                            <p class="no-results-hint">Please check your search for any spelling mistakes, or try using different keywords.</p>
                        </div>
                    `;
                },
                showMoreText(results, { html }) {
                    return html`
                        <span class="btn btn-dark">
                            Load more results
                        </span>
                    `;
                },
            },
            cssClasses: {
                root: 'search-page-hits',
                list: 'search-results-grid',
                loadMore: 'load-more-btn',
            },
        }),
    ]);

    search.start();

    // Input state handling
    const input = document.querySelector('#searchbox-page .ais-SearchBox-input');
    const form = document.querySelector('#searchbox-page .ais-SearchBox-form');

    if (input && form) {
        if (input.value.length > 0) {
            form.classList.add('has-value');
        }

        input.addEventListener('input', function() {
            if (input.value.length > 0) {
                form.classList.add('has-value');
            } else {
                form.classList.remove('has-value');
            }
        });

        const resetBtn = form.querySelector('.ais-SearchBox-reset');
        if (resetBtn) {
            resetBtn.addEventListener('click', function() {
                form.classList.remove('has-value');
            });
        }
    }
}